// frontend/src/scripts/components/confirm-modal.ts

export interface ConfirmModalOptions {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
}

export function showConfirmModal(opts: ConfirmModalOptions): void {
  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'presentation');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 120ms ease-out;
  `;

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  const titleId = `confirm-modal-title-${Date.now()}`;
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.style.cssText = `
    background: #fff;
    color: #0f172a;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
    width: min(420px, calc(100% - 2rem));
    padding: 1.25rem 1.25rem 1rem;
    font-family: inherit;
  `;

  const heading = document.createElement('h2');
  heading.id = titleId;
  heading.textContent = opts.title;
  heading.style.cssText = 'margin: 0 0 0.5rem; font-size: 1.1rem;';

  const body = document.createElement('p');
  body.textContent = opts.message;
  body.style.cssText = 'margin: 0 0 1.25rem; color: #475569; line-height: 1.4;';

  const actions = document.createElement('div');
  actions.style.cssText = 'display: flex; justify-content: flex-end; gap: 0.5rem;';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.textContent = opts.cancelLabel;
  cancelButton.style.cssText = `
    padding: 0.5rem 0.9rem;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font: inherit;
  `;

  const confirmButton = document.createElement('button');
  confirmButton.type = 'button';
  confirmButton.textContent = opts.confirmLabel;
  confirmButton.style.cssText = `
    padding: 0.5rem 0.9rem;
    border: 1px solid #b91c1c;
    border-radius: 6px;
    background: #dc2626;
    color: #fff;
    cursor: pointer;
    font: inherit;
  `;

  actions.appendChild(cancelButton);
  actions.appendChild(confirmButton);
  dialog.appendChild(heading);
  dialog.appendChild(body);
  dialog.appendChild(actions);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  // Fade in on next frame so the transition runs.
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });

  function close(): void {
    document.removeEventListener('keydown', onKeydown, true);
    overlay.remove();
    if (previouslyFocused && document.body.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === 'Tab') {
      const focusableButtons = [cancelButton, confirmButton];
      const currentIndex = focusableButtons.indexOf(document.activeElement as HTMLButtonElement);
      const nextIndex = e.shiftKey
        ? (currentIndex <= 0 ? focusableButtons.length - 1 : currentIndex - 1)
        : (currentIndex === focusableButtons.length - 1 ? 0 : currentIndex + 1);

      e.preventDefault();
      focusableButtons[nextIndex].focus();
    }
  }

  cancelButton.addEventListener('click', () => close());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      close();
    }
  });
  confirmButton.addEventListener('click', () => {
    close();
    opts.onConfirm();
  });
  document.addEventListener('keydown', onKeydown, true);

  // Focus the Cancel button so Enter does not destructively confirm by default.
  cancelButton.focus();
}
